import React from 'react';
import ReactDOM from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { CEB_COLOR } from 'app-constants/CEB_COLOR';

export function ConditionalTooltip({ condition, visible, title, children }) {
  return condition ? (
    <Tooltip visible={visible} title={title}>
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );
}

export function Tooltip(props) {
  const [show, setShow] = React.useState(props.visible);

  React.useEffect(() => {
    setShow(props.visible);
  }, [props.visible]);

  const onEnter = React.useCallback(() => {
    return setShow(true);
  });

  const onLeave = React.useCallback(() => {
    return setShow(false);
  });

  const [tooltipTarget, setTooltipTarget] = React.useState();

  const onSetTooltipTarget = React.useCallback(target => {
    setTooltipTarget(target);
  });

  React.useEffect(() => {
    if (tooltipTarget) {
      tooltipTarget.addEventListener('mouseenter', onEnter);
      tooltipTarget.addEventListener('mouseleave', onLeave);
    }

    return () => {
      if (tooltipTarget) {
        tooltipTarget.removeEventListener('mouseenter', onEnter);
        tooltipTarget.removeEventListener('mouseleave', onLeave);
      }
    };
  }, [tooltipTarget]);

  return (
    <StyledTooltip
      className="tooltip-target"
      inline={props.inline}
      ref={onSetTooltipTarget}
    >
      {props.children}
      {show && props.title ? (
        <TooltipRenderer title={props.title} target={tooltipTarget} />
      ) : null}
    </StyledTooltip>
  );
}

const StyledTooltip = styled.div.attrs(({ inline }) => ({ inline }))`
  position: relative;
  ${({ inline }) => (inline ? 'display:inline;' : '')}
`;

function TooltipRenderer({ target, title }) {
  const { x, y, width } = React.useMemo(() => {
    return (
      target?.getBoundingClientRect() || {
        x: 0,
        y: 0,
        width: 0,
      }
    );
  }, [target]);

  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  return ReactDOM.createPortal(
    <>
      <div className="tooltip-container">
        <div className="tooltip">{title}</div>
      </div>
      <StyledTooltipRenderer
        x={x}
        y={y}
        width={width}
        scrollTop={scrollTop}
        scrollLeft={scrollLeft}
      />
    </>,
    document.body,
  );
}

export const StyledTooltipRenderer = createGlobalStyle`
  .tooltip-container {
    top: ${({ y, scrollTop }) => y + scrollTop}px;
    left: ${({ x, scrollLeft }) => x + scrollLeft}px;
    width: ${({ width }) => width}px;
    height: 0;
    position: absolute;
    z-index: 101;
    > .tooltip {
      background: white;
      border: solid 1px ${CEB_COLOR('MERCURY')};
      bottom: 100%;
      color: ${CEB_COLOR('BLACK')};
      font-family: 'Zilla Slab', serif;
      font-size: 14px;
      font-weight: bold;
      left: 50%;
      line-height: 2.29;
      margin-bottom: 4px;
      margin-left: 10px;
      min-width: 100%;
      padding: 0 20px;
      position: absolute;
      text-align: center;
      transform-origin: top;
      transform: translate(-50%, 0px);
      white-space: nowrap;
      z-index: 1;
      &:after,
      &:before {
        border: solid transparent;
        content: ' ';
        height: 0;
        left: 50%;
        pointer-events: none;
        position: absolute;
        top: 100%;
        width: 0;
      }

      &:after {
        border-color: rgba(255, 255, 255, 0);
        border-top-color: #ffffff;
        border-width: 4px;
        margin-left: -4px;
      }
      &:before {
        border-color: rgba(194, 225, 245, 0);
        border-top-color: ${CEB_COLOR('MERCURY')};
        border-width: 5px;
        margin-left: -5px;
      }
    }
  }
`;
